/**
 * F-180 (B2) — specialist-facing product list / CRUD page.
 *
 * Mirrors `MyPresetsPage` shape: list with EmptyState fallback,
 * `useMainButton` to launch the create modal, per-row edit + delete.
 * `is_active` is exposed as an inline toggle so a specialist can quickly
 * hide a row without re-opening the editor.
 */
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Icon,
  Spinner,
} from '@/components/atoms';
import { ProductEditModal } from '@/components/domain';
import { useBackButton, useHapticFeedback, useMainButton } from '@/hooks';
import { products as productsApi } from '@/api';
import type { ProductInput } from '@/api/products';
import type { ProductOffering } from '@/types/domain';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import { resolveMediaUrl } from '@/utils/media';
import styles from './pages.module.css';

function formatPrice(value: number | string, isFrom: boolean): string {
  const num = Number(value).toFixed(2);
  return isFrom ? `від ${num} €` : `${num} €`;
}

export function MyProductsPage(): JSX.Element {
  useBackButton();
  const haptic = useHapticFeedback();
  const [items, setItems] = useState<ProductOffering[] | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ProductOffering | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProductOffering | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const data = await productsApi.listMyProducts();
      setItems(data.results);
    } catch (err) {
      logger.warn('Products fetch failed', err);
      setItems([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (product: ProductOffering): void => {
    setEditing(product);
    setEditorOpen(true);
  };

  const closeEditor = (): void => {
    setEditorOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (payload: ProductInput): Promise<void> => {
    try {
      if (editing) {
        await productsApi.updateProduct(editing.id, payload);
        toast.success('Збережено');
      } else {
        await productsApi.createProduct(payload);
        toast.success('Товар додано');
      }
      haptic.notification('success');
      closeEditor();
      await load();
    } catch (err) {
      logger.error('Product save failed', err);
      toast.error('Не вдалось зберегти');
      haptic.notification('error');
    }
  };

  const toggleActive = async (product: ProductOffering): Promise<void> => {
    try {
      await productsApi.updateProduct(product.id, { is_active: !product.is_active });
      setItems((prev) =>
        prev?.map((p) => (p.id === product.id ? { ...p, is_active: !p.is_active } : p)) ?? prev,
      );
      haptic.selectionChanged();
    } catch (err) {
      logger.warn('Toggle active failed', err);
      toast.error('Не вдалось оновити');
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await productsApi.deleteProduct(pendingDelete.id);
      toast.success('Видалено');
      haptic.notification('success');
      setPendingDelete(null);
      await load();
    } catch (err) {
      logger.error('Product delete failed', err);
      toast.error('Не вдалось видалити');
    } finally {
      setDeleting(false);
    }
  };

  useMainButton({ text: 'Додати товар', onClick: openCreate });

  return (
    <Layout title="Товари">
      {items === null ? (
        <Spinner center />
      ) : items.length === 0 ? (
        <EmptyState
          icon="package"
          message="Жодного товару. Додай — клієнти зможуть купити прямо з профілю."
          actions={<Button onClick={openCreate}>Додати товар</Button>}
        />
      ) : (
        <div className={styles.list}>
          {items.map((p) => (
            <Card key={p.id}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                {p.image_url ? (
                  <img
                    src={resolveMediaUrl(p.image_url)}
                    alt={p.title}
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 8,
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                    loading="lazy"
                  />
                ) : (
                  <div
                    aria-hidden
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 8,
                      background: 'var(--bg-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="package" size={28} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 8,
                    }}
                  >
                    <strong
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        opacity: p.is_active ? 1 : 0.55,
                      }}
                    >
                      {p.title}
                    </strong>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {formatPrice(p.price_eur, p.price_is_from)}
                    </span>
                  </div>
                  <div className={styles.note}>
                    {p.stock == null
                      ? 'Unlimited'
                      : p.stock <= 0
                        ? 'Немає в наявності'
                        : `В наявності: ${p.stock} шт.`}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      marginTop: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={p.is_active}
                        onChange={() => void toggleActive(p)}
                      />
                      Активний
                    </label>
                    <Button variant="ghost" onClick={() => openEdit(p)}>
                      Редагувати
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setPendingDelete(p)}
                    >
                      Видалити
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ProductEditModal
        open={editorOpen}
        product={editing}
        onClose={closeEditor}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Видалити товар?"
        body={
          pendingDelete ? (
            <p>
              «{pendingDelete.title}» більше не з'являтиметься в профілі. Скасувати дію
              не вийде.
            </p>
          ) : null
        }
        confirmLabel="Видалити"
        cancelLabel="Скасувати"
        tone="destructive"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Layout>
  );
}
